import { track } from "@amplitude/analytics-browser";
import { Grid, LinearProgress, Typography } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Slider } from "../../../components/slider/slider.component";
import { progressAtom } from "../../../components/wizard/atoms";
import { FooterActions } from "../../../components/wizard/footer-actions.component";
import type {
  GamePlanItem,
  GamePlanPath,
  GamePlanSlide,
} from "../../../components/wizard/use-game-plan";
import {
  type AmplitudeEvent,
  AmplitudeEventType,
  type AmplitudeTrack,
  type GamePlanEventProperties,
} from "../../../models/amplitude";
import { Status } from "../../../models/gameplan";
import {
  useInIframe,
  useUpdateBffLearning,
  useUpdateLearningPathProgress,
  useUpdateParticipantActivity,
} from "../../../shared/hooks";
import {
  setLessonStatusById,
  setModuleStatusById,
} from "../../../shared/slices/data";
import type { RootState } from "../../../store";
import { setDiscoverStatus } from "../../learningPath.service";

type Selector = {
  participantProfileId: RootState["profile"]["participantProfileId"];
};

type Props = {
  isLearningPathStarted?: boolean | undefined;
  gamePlan: GamePlanPath;
};

export const Discover = ({ gamePlan, isLearningPathStarted }: Props) => {
  const dispatch = useDispatch();
  const isInIframe = useInIframe();
  const { module, item, parent } = gamePlan;
  const gamePlanSlides = item as GamePlanSlide[];
  const { order: lessonOrder, id: lessonId } = parent.item as GamePlanItem;
  const [allRead, setAllRead] = useState(gamePlanSlides.length == 1);
  const { updateModuleStatus } = useUpdateBffLearning();
  const { addTodayActivity } = useUpdateParticipantActivity();
  const { invalidateCache: invalidateCacheLearningPathProgress } =
    useUpdateLearningPathProgress();

  const { participantProfileId } = useSelector<RootState, Selector>(
    (state) => ({
      participantProfileId: state.profile.participantProfileId,
    })
  );

  const sharedAmplitudeData: AmplitudeEvent<GamePlanEventProperties> = {
    user_id: participantProfileId,
    event_properties: {
      page_url: window.location.href,
      learning_path_name: module.learningPath.name,
      course_name: module.course.name,
      module_name: module.title,
      lesson_id: lessonId,
      lesson_name: parent.title,
    },
  };

  const {
    mutate,
    mutateAsync,
    isLoading: isUpdating,
  } = useMutation(setDiscoverStatus, {
    onSuccess: (_, variables) => {
      if (module.status === Status.NEW) {
        const moduleId = module.item.id;
        dispatch(setModuleStatusById(moduleId, Status.IN_PROGRESS));
        updateModuleStatus(moduleId, Status.IN_PROGRESS);
      }

      dispatch(setLessonStatusById(variables.lessonId, variables.slideStatus));
      addTodayActivity();

      if (!isLearningPathStarted) {
        const learningPathStartedEvent: AmplitudeTrack<AmplitudeEventType.LEARNING_PATH_STARTED> =
          {
            event_type: AmplitudeEventType.LEARNING_PATH_STARTED,
            user_id: participantProfileId,
            event_properties: {
              page_url: window.location.href,
              learning_path_id: module.learningPath.id,
              learning_path_name: module.learningPath.name,
            },
          };
        track(learningPathStartedEvent);
        invalidateCacheLearningPathProgress();
      }

      const slideStatus = variables.slideStatus as Status;
      const amplitudeEventType =
        slideStatus === Status.COMPLETED
          ? AmplitudeEventType.LESSON_COMPLETED
          : AmplitudeEventType.LESSON_STARTED;

      const amplitudeEvent: AmplitudeTrack<typeof amplitudeEventType> = {
        event_type: amplitudeEventType,
        ...sharedAmplitudeData,
      };
      track(amplitudeEvent);
    },
  });

  const isIntro = lessonOrder === 0;
  const setProgress = useSetAtom(progressAtom);

  useEffect(() => {
    setProgress(isIntro ? 10 : 75);
  }, [isIntro, setProgress]);

  useEffect(() => {
    if (gamePlan.status === Status.NEW) {
      mutate({
        lessonId: lessonId,
        slideStatus: Status.IN_PROGRESS,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextButtonAction = async () => {
    if (gamePlan.status !== Status.COMPLETED) {
      await mutateAsync({
        lessonId: lessonId,
        slideStatus: Status.COMPLETED,
      });
    }
  };

  return (
    <>
      <Grid container>
        {isUpdating && (
          <div data-testid="loading">
            <LinearProgress color="secondary" />
          </div>
        )}
        <Grid item xs={12}>
          <Typography variant="h6" className="title">
            {isIntro ? "Intro" : "Discover"}
          </Typography>
        </Grid>
        <Slider
          amplitudeData={sharedAmplitudeData}
          slides={gamePlanSlides}
          showIcons={!isIntro && !isInIframe}
          onBottomScroll={() => setAllRead(true)}
        />
      </Grid>
      <FooterActions
        nextDisabled={!allRead || isUpdating}
        nextAction={nextButtonAction}
      />
    </>
  );
};
